import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckCheckIcon, CheckIcon, XIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import type { AttendanceMark, Voice } from '../../types';
import { eventTitle, eventTypeClass, eventTypeShort, formatDate, fullName, voiceLabel, voiceOrder } from '../../utils/labels';
export function AdminAttendanceTake() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    events,
    expectedFor,
    attendance,
    saveAttendance,
    rsvps,
    pushToast
  } = useApp();
  const event = events.find(e => e.id === id);
  const pricakovani = useMemo(() => event ? expectedFor(event) : [], [event, expectedFor]);
  const [marks, setMarks] = useState<Record<string, AttendanceMark>>(() => {
    if (!event) return {};
    const shranjeno = attendance[event.id];
    if (shranjeno) return {
      ...shranjeno
    };
    const zacetno: Record<string, AttendanceMark> = {};
    expectedFor(event).forEach(m => {
      zacetno[m.id] = rsvps[`${m.id}:${event.id}`]?.status === 'ne_morem' ? 'odsoten' : 'prisoten';
    });
    return zacetno;
  });
  if (!event) {
    return <Card className="p-10 text-center" data-id="element-659" data-mp="1rofbh1mdycy4">
        <p className="text-sm font-medium text-ink" data-id="element-660" data-mp="1wmqkja4uwjfa">Dogodek ne obstaja.</p>
        <Link to="/admin/prisotnost" className="mt-2 inline-block text-[13px] font-medium text-green-600" data-id="element-661" data-mp="1wol4om8b07ib">
          Nazaj
        </Link>
      </Card>;
  }
  const poGlasovih = voiceOrder.map(v => ({
    voice: v as Voice,
    clani: pricakovani.filter(m => m.voice === v)
  })).filter(g => g.clani.length > 0);
  const prisotnih = pricakovani.filter(m => marks[m.id] !== 'odsoten').length;
  const odsotnih = pricakovani.length - prisotnih;
  function nastavi(memberId: string, mark: AttendanceMark) {
    setMarks(m => ({
      ...m,
      [memberId]: mark
    }));
  }
  function vsiPrisotni() {
    const novi: Record<string, AttendanceMark> = {};
    pricakovani.forEach(m => {
      novi[m.id] = 'prisoten';
    });
    setMarks(novi);
  }
  return <div className="mx-auto max-w-4xl space-y-4 pb-24" data-id="element-662" data-mp="1skm8v648qdii">
      <div className="flex flex-wrap items-center gap-4" data-id="element-663" data-mp="1ohzasngz7wcv">
        <button onClick={() => navigate('/admin/prisotnost')} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-664" data-mp="1ogehf16eejgl">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-665" data-mp="2c7k685ubgm31" />
          Nazaj na prisotnost
        </button>
        <Link to={`/admin/dogodki/${event.id}`} className="text-[13px] font-medium text-green-600 hover:underline" data-id="element-666" data-mp="2c9d0smrnnfuo">
          Odpri dogodek
        </Link>
      </div>

      <Card className="p-5" data-id="element-667" data-mp="2l5vtpyzvz3r9">
        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-668" data-mp="2g9niuxsq1wpw">
          <div data-id="element-669" data-mp="2g7auo2wdeq4e">
            <div className="flex flex-wrap items-center gap-1.5" data-id="element-670" data-mp="2zv6zlx5alqea">
              <Badge className={eventTypeClass[event.type]} data-id="element-671" data-mp="2zsuh9ivmhe0p">{eventTypeShort[event.type]}</Badge>
              {event.opomba && <Badge className="bg-canvas text-ink-muted border-line" data-id="element-672" data-mp="34rnurbawt50v">{event.opomba}</Badge>}
            </div>
            <h1 className="mt-2 text-xl font-semibold text-ink" data-id="element-673" data-mp="2uvkkfi5wm02u">{eventTitle(event)}</h1>
            <p className="mt-1 text-[13px] text-ink-muted" data-id="element-674" data-mp="2uxovql64u1d5">
              {formatDate(event.datum, {
              weekday: true
            })} · {event.zacetek} · {event.lokacija}
            </p>
          </div>
          <Button variant="secondary" onClick={vsiPrisotni} data-id="element-675" data-mp="2lx8xpr85rmfo">
            <CheckCheckIcon className="h-4 w-4" data-id="element-676" data-mp="277kv8mo8qhqd" />
            Označi vse prisotne
          </Button>
        </div>
      </Card>

      <div className="space-y-3" data-id="element-677" data-mp="2c5md549xkb09">
        {poGlasovih.map(({
        voice,
        clani
      }) => <Card key={voice} className="overflow-hidden" data-id="element-678" data-mp="1y63jfghv927h">
            <div className="flex items-center justify-between border-b border-line bg-canvas px-4 py-2.5" data-id="element-679" data-mp="2356d64sebm05">
              <h2 className="text-[13px] font-semibold uppercase tracking-wide text-ink" data-id="element-680" data-mp="1v48gi4fv9qtl">
                {voiceLabel[voice]}
              </h2>
              <span className="text-[12px] text-ink-muted" data-id="element-681" data-mp="1q5nogsarqdg8">
                {clani.filter(m => marks[m.id] !== 'odsoten').length}/{clani.length} prisotnih
              </span>
            </div>
            <ul className="divide-y divide-line" data-id="element-682" data-mp="1hcbrwadepz1y">
              {clani.map(m => {
            const napovedana = rsvps[`${m.id}:${event.id}`]?.status === 'ne_morem';
            const mark = marks[m.id] ?? 'prisoten';
            return <li key={m.id} className="flex items-center gap-3 px-4 py-2.5" data-id="element-683" data-mp="12ejdjljljgpc">
                    <span className="min-w-0 flex-1" data-id="element-684" data-mp="ydziqomhwp6d">
                      <span className="block truncate text-sm font-medium text-ink" data-id="element-685" data-mp="yfiy8d0buw73">{fullName(m)}</span>
                      {napovedana && <span className="mt-0.5 inline-flex text-[12px] text-amber-700" data-id="element-686" data-mp="2ss0h78e4va5x">
                          Napovedana odsotnost
                          {rsvps[`${m.id}:${event.id}`]?.razlog ? ` — ${rsvps[`${m.id}:${event.id}`]?.razlog?.toLowerCase()}` : ''}
                        </span>}
                    </span>
                    <div className="flex shrink-0 gap-1.5" data-id="element-687" data-mp="sbw72f846xx8">
                      <button onClick={() => nastavi(m.id, 'prisoten')} aria-pressed={mark === 'prisoten'} aria-label={`${fullName(m)} prisoten`} className={`flex h-10 w-14 items-center justify-center rounded-lg border transition-colors duration-150 ease-out ${mark === 'prisoten' ? 'border-green-500 bg-green-500 text-white' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-688" data-mp="sdh183qioeuk">
                        <CheckIcon className="h-4 w-4" data-id="element-689" data-mp="1l1uygh9s7752" />
                      </button>
                      <button onClick={() => nastavi(m.id, 'odsoten')} aria-pressed={mark === 'odsoten'} aria-label={`${fullName(m)} odsoten`} className={`flex h-10 w-14 items-center justify-center rounded-lg border transition-colors duration-150 ease-out ${mark === 'odsoten' ? 'border-rose-500 bg-rose-500 text-white' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-690" data-mp="3h59ils9bm8oh">
                        <XIcon className="h-4 w-4" data-id="element-691" data-mp="384bezb2so36f" />
                      </button>
                    </div>
                  </li>;
          })}
            </ul>
          </Card>)}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-white px-4 py-3 lg:left-64" data-id="element-692" data-mp="2andzvodt7tlb">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4" data-id="element-693" data-mp="q5bsfkcpb2t8">
          <div className="text-sm" data-id="element-694" data-mp="uvsa5gnk1ale">
            <span className="font-semibold text-green-600" data-id="element-695" data-mp="uwtw4g63zhhw">{prisotnih} prisotnih</span>
            <span className="mx-2 text-line" data-id="element-696" data-mp="h56ndguzcgi8">·</span>
            <span className="font-semibold text-rose-600" data-id="element-697" data-mp="h2ardy2p01wf">{odsotnih} odsotnih</span>
            <span className="ml-2 text-ink-muted" data-id="element-698" data-mp="l68pc5ga76nw">od {pricakovani.length}</span>
          </div>
          <Button onClick={() => {
          saveAttendance(event.id, marks);
          pushToast('Prisotnost je shranjena.');
          navigate('/admin/prisotnost');
        }} data-id="element-699" data-mp="18w308hh1il5i">
            Shrani prisotnost
          </Button>
        </div>
      </div>
    </div>;
}